import React from 'react';
import { ArrowRightIcon } from 'lucide-react';
import { companyLinks, designServices, marketingGroups, marketingMenuFeature, marketingServices } from '../../data/navigation';
import type { NavSubItem } from '../../data/navigation';
import { gallerySites } from '../../data/gallery';
import { NavIconGlyph } from './NavIconGlyph';
type PanelProps = {
  onNavigate: () => void;
};
const featuredSites = gallerySites.filter(site => site.featured).slice(0, 3);
function MenuLink({
  item,
  onNavigate,
  large = false
}: {
  item: NavSubItem;
  onNavigate: () => void;
  large?: boolean;
}) {
  return <a href={item.href} onClick={onNavigate} className="group flex items-start gap-3 rounded-lg p-2.5 transition-colors duration-150 ease-out hover:bg-brand-tint focus:outline-none focus-visible:bg-brand-tint" data-id="element-631" data-mp="7hf4rlapdwnq">
      <span className={`flex flex-shrink-0 items-center justify-center rounded-md bg-brand-light text-brand-black transition-colors duration-150 ease-out group-hover:bg-brand-red group-hover:text-white ${large ? 'h-11 w-11' : 'h-9 w-9'}`} data-id="element-632" data-mp="299k1rb727k5b">
        <NavIconGlyph icon={item.icon} className={large ? 'h-5 w-5' : 'h-[18px] w-[18px]'} data-id="element-633" data-mp="2ltni24s0bg1y" />
      </span>
      <span className="min-w-0" data-id="element-634" data-mp="2guaxxrsisn32">
        <span className={`block font-semibold text-brand-black ${large ? 'text-base' : 'text-sm'}`} data-id="element-635" data-mp="2gvlxtgiuhaow">{item.name}</span>
        <span className={`mt-0.5 block text-brand-gray ${large ? 'text-sm' : 'text-xs'}`} data-id="element-636" data-mp="2crebfpzv06tu">{item.description}</span>
      </span>
    </a>;
}
export function MarketingMegaPanel({
  onNavigate
}: PanelProps) {
  const byName = new Map(marketingServices.map(item => [item.name, item]));
  const feature = marketingMenuFeature;
  return <div className="grid grid-cols-12 gap-8" data-id="element-637" data-mp="2vki69eotcpq1">
      <div className="col-span-8 grid grid-cols-3 gap-6" data-id="element-638" data-mp="2vjgxjgv9sfbh">
        {marketingGroups.map(group => <div key={group.title} data-id="element-639" data-mp="1p57s47kzj4ob">
            <p className="px-2.5 text-xs font-bold uppercase tracking-[0.14em] text-brand-red" data-id="element-640" data-mp="1k4tm0v2y24sx">{group.title}</p>
            <ul className="mt-3 space-y-1" data-id="element-641" data-mp="1llh41b87pj2m">
              {group.items.map(name => {
            const item = byName.get(name);
            return item ? <li key={name} data-id="element-642" data-mp="1qipojigu1z71">
                    <MenuLink item={item} onNavigate={onNavigate} data-id="element-643" data-mp="1clu3ssr7yjb5" />
                  </li> : null;
          })}
            </ul>
          </div>)}
      </div>

      <a href={feature.href} onClick={onNavigate} className="group relative col-span-4 flex min-h-[260px] overflow-hidden rounded-xl bg-brand-black focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-red focus-visible:ring-offset-2" data-id="element-644" data-mp="12vrassk3l5ct">
        <img src={feature.image} alt="" decoding="async" className="absolute inset-0 h-full w-full object-cover opacity-50 transition-transform duration-300 ease-out group-hover:scale-[1.03]" data-id="element-645" data-mp="svgugz2rnhtf" />
        <span className="relative mt-auto block p-6" data-id="element-646" data-mp="ovzqka2zmsqd">
          <span className="block text-xs font-bold uppercase tracking-[0.14em] text-white" data-id="element-647" data-mp="oxj61ygtkzr3">{feature.kicker}</span>
          <span className="mt-2 block text-xl font-extrabold leading-snug text-white" data-id="element-648" data-mp="atn8aop86gi">{feature.title}</span>
          <span className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-brand-red px-4 py-2.5 text-sm font-bold text-white transition-colors duration-150 ease-out group-hover:bg-brand-red-dark" data-id="element-649" data-mp="eacul19ordrr">
            {feature.cta}
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-650" data-mp="o7qxrz6lm624" />
          </span>
        </span>
      </a>
    </div>;
}
export function DesignMegaPanel({
  onNavigate
}: PanelProps) {
  return <div className="grid grid-cols-12 gap-8" data-id="element-651" data-mp="xxk6msfcw72o">
      <div className="col-span-4" data-id="element-652" data-mp="y0esmoe0pzy7">
        <p className="px-2.5 text-xs font-bold uppercase tracking-[0.14em] text-brand-red" data-id="element-653" data-mp="syz9j1vei3ig">Web Design</p>
        <ul className="mt-3 space-y-1" data-id="element-654" data-mp="16yifpp4nj4s4">
          {designServices.map(item => <li key={item.name} data-id="element-655" data-mp="120p9iw3cmaru">
              <MenuLink item={item} onNavigate={onNavigate} large data-id="element-656" data-mp="121i5r34olhvq" />
            </li>)}
        </ul>
        <p className="mt-5 border-t border-gray-100 px-2.5 pt-5 text-sm leading-relaxed text-gray-600" data-id="element-657" data-mp="2wfw1kvinmoym">
          Built in 3 days. Changed in minutes by phone, text, email, Slack or online.
        </p>
      </div>

      <div className="col-span-8" data-id="element-658" data-mp="3670e7okuxxqa">
        <div className="flex items-baseline justify-between px-0.5" data-id="element-659" data-mp="3a7kdaxozrbf2">
          <p className="text-xs font-bold uppercase tracking-[0.14em] text-brand-red" data-id="element-660" data-mp="3a9wqkxjt0xls">Recent work</p>
          <a href="#/design/gallery" onClick={onNavigate} className="group inline-flex items-center gap-1 text-sm font-bold text-brand-black transition-colors duration-150 ease-out hover:text-brand-red focus:outline-none focus-visible:underline" data-id="element-661" data-mp="2djmdrghyak1f">
            See all {gallerySites.length} sites
            <ArrowRightIcon className="h-4 w-4 transition-transform duration-150 ease-out group-hover:translate-x-0.5" aria-hidden="true" data-id="element-662" data-mp="1ss9errbquhu" />
          </a>
        </div>
        <ul className="mt-3 grid grid-cols-3 gap-4" data-id="element-663" data-mp="gv0qx6v93c31">
          {featuredSites.map(site => <li key={site.id} data-id="element-664" data-mp="gwv41z0a5zwk">
              <a href="#/design/gallery" onClick={onNavigate} className="group block focus:outline-none" data-id="element-665" data-mp="bvnsdov0ogkm">
                <span className="block aspect-[4/3] overflow-hidden rounded-lg border border-gray-200 bg-gray-100 group-focus-visible:ring-2 group-focus-visible:ring-brand-red" data-id="element-666" data-mp="33lvu1o2jkfm">
                  <img src={site.image} alt="" loading="lazy" className="h-full w-full object-cover object-top transition-transform duration-300 ease-out group-hover:scale-[1.03]" data-id="element-667" data-mp="3u7u10x58vxub" />
                </span>
                <span className="mt-2 block text-sm font-semibold text-brand-black transition-colors duration-150 ease-out group-hover:text-brand-red" data-id="element-668" data-mp="3gc8in8tiocav">
                  {site.name}
                </span>
                <span className="block text-xs text-brand-gray" data-id="element-669" data-mp="1egy2slf4kj60">{site.industry}</span>
              </a>
            </li>)}
        </ul>
      </div>
    </div>;
}
export function CompanyMegaPanel({
  onNavigate
}: PanelProps) {
  return <div className="grid grid-cols-12 gap-8" data-id="element-670" data-mp="2z1mh0mvavlf9">
      <div className="col-span-4" data-id="element-671" data-mp="2yyqhwv7puqp8">
        <p className="px-2.5 text-xs font-bold uppercase tracking-[0.14em] text-brand-red" data-id="element-672" data-mp="331wvknx5rk5q">Company</p>
        {/* ABOUT US: future spot. It is listed (commented out) in companyLinks in data/navigation.ts. */}
        <ul className="mt-3 space-y-1" data-id="element-673" data-mp="3vrc7dfwndh2p">
          {companyLinks.map(item => <li key={item.name} data-id="element-674" data-mp="3hrkw3mximese">
              <MenuLink item={item} onNavigate={onNavigate} large data-id="element-675" data-mp="3hqj9qmb1u5vd" />
            </li>)}
        </ul>
      </div>

      <a href="#/partners" onClick={onNavigate} className="group relative col-span-8 flex min-h-[220px] overflow-hidden rounded-xl bg-brand-black focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-red focus-visible:ring-offset-2" data-id="element-676" data-mp="3moksf64kc36b">
        <img src={marketingMenuFeature.image} alt="" decoding="async" className="absolute inset-0 h-full w-full object-cover opacity-40 transition-transform duration-300 ease-out group-hover:scale-[1.03]" data-id="element-677" data-mp="22t6trx1184kg" />
        <span className="relative mt-auto block p-6" data-id="element-678" data-mp="27tbejz8z3lgh">
          <span className="block text-xs font-bold uppercase tracking-[0.14em] text-white" data-id="element-679" data-mp="27tumqo8tmvhb">Partner Center</span>
          <span className="mt-2 block max-w-[36ch] text-xl font-extrabold leading-snug text-white" data-id="element-680" data-mp="1k40944goh2w0">
            Sell our websites, marketing and monthly plans. Get paid every month.
          </span>
          <span className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-brand-red px-4 py-2.5 text-sm font-bold text-white transition-colors duration-150 ease-out group-hover:bg-brand-red-dark" data-id="element-681" data-mp="1tuuz9jx1ommq">
            Visit the Partner Center
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-682" data-mp="wefolh4vlxkm" />
          </span>
        </span>
      </a>
    </div>;
}